/** Broad categories used in the insights filter bar and admin dropdown. */
export const INSIGHT_FILTER_CATEGORIES = [
  "Tax",
  "Business Advisory",
  "Regulatory",
  "Accounting & Reporting",
  "Industry Focus",
  "Thought Leadership",
  "ESG",
  "Audit",
] as const;

/** Suggested topic tags for cards and popular topics (admin + search). */
export const INSIGHT_TAG_SUGGESTIONS = [
  "Digital",
  "Accounting",
  "Valuation",
  "Advisory",
  "Budget 2025",
  "Tax Incentives",
  "SST Updates",
  "Corporate Compliance",
  "Financial Reporting",
  "Business Growth",
  "Sustainability",
] as const;

/** Maps filter-bar labels to category values stored on insights. */
const FILTER_ALIASES: Record<string, string[]> = {
  tax: ["tax"],
  "business advisory": ["business advisory", "advisory"],
  regulatory: ["regulatory", "regulation", "compliance"],
  "accounting & reporting": ["accounting & reporting", "accounting", "financial reporting"],
  "industry focus": ["industry focus", "industry"],
  "thought leadership": ["thought leadership"],
  esg: ["esg", "sustainability"],
  audit: ["audit", "assurance"],
};

function normalize(value: string) {
  return value.trim().toLowerCase();
}

export function insightMatchesTopic(
  article: { tags?: string[] },
  topic: string,
): boolean {
  const needle = normalize(topic);
  if (!needle) return true;
  return (article.tags ?? []).some((tag) => normalize(tag) === needle);
}

export function insightMatchesFilter(articleCategory: string, filter: string): boolean {
  if (filter === "All Insights") return true;
  const cat = normalize(articleCategory);
  if (!cat) return false;

  const aliases = FILTER_ALIASES[normalize(filter)] ?? [normalize(filter)];
  return aliases.some((alias) => cat === alias || cat.includes(alias) || alias.includes(cat));
}

export function insightMatchesSearch(
  article: { title: string; excerpt: string; category: string; tags?: string[] },
  query: string,
): boolean {
  const q = normalize(query);
  if (!q) return true;

  for (const tag of article.tags ?? []) {
    const t = normalize(tag);
    if (!t) continue;
    if (t === q || t.includes(q) || q.includes(t)) return true;
  }

  const haystack = [article.title, article.excerpt, article.category].join(" ").toLowerCase();
  return haystack.includes(q);
}

export function insightDisplayLabel(article: { category: string; tags?: string[] }): string {
  const tag = article.tags?.find((t) => t.trim());
  return tag?.trim() || article.category || "Insight";
}

export function parseInsightTagsInput(value: string): string[] {
  return [...new Set(value.split(",").map((t) => t.trim()).filter(Boolean))];
}

export function formatInsightTagsInput(tags: string[] | null | undefined): string {
  return (tags ?? []).join(", ");
}

/** Top insight categories by article count (for mega menu topics). */
export function collectTopInsightCategories(
  articles: Array<{ category: string }>,
  limit = 5,
): string[] {
  const counts = new Map<string, number>();
  for (const article of articles) {
    const cat = article.category?.trim();
    if (!cat) continue;
    counts.set(cat, (counts.get(cat) ?? 0) + 1);
  }
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([cat]) => cat)
    .slice(0, limit);
}

const ALL_INSIGHTS_FILTER = "All Insights" as const;

/** Map a stored category (or filter label) to the insights page filter bar value. */
export function resolveInsightFilterFromCategory(
  category: string,
): (typeof INSIGHT_FILTER_CATEGORIES)[number] | typeof ALL_INSIGHTS_FILTER {
  const trimmed = category.trim();
  if (!trimmed) return ALL_INSIGHTS_FILTER;

  if ((INSIGHT_FILTER_CATEGORIES as readonly string[]).includes(trimmed)) {
    return trimmed as (typeof INSIGHT_FILTER_CATEGORIES)[number];
  }

  for (const filter of INSIGHT_FILTER_CATEGORIES) {
    if (insightMatchesFilter(trimmed, filter)) {
      return filter;
    }
  }

  return ALL_INSIGHTS_FILTER;
}

export function collectPopularTopics(
  articles: Array<{ tags?: string[] }>,
  featuredTags: string[] = [],
  limit = 8,
): string[] {
  const featured = featuredTags.map((tag) => tag.trim()).filter(Boolean);

  const counts = new Map<string, number>();
  for (const article of articles) {
    for (const tag of article.tags ?? []) {
      const trimmed = tag.trim();
      if (!trimmed) continue;
      counts.set(trimmed, (counts.get(trimmed) ?? 0) + 1);
    }
  }
  const fromArticles = [...counts.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([tag]) => tag);

  const merged = [...featured];
  for (const tag of fromArticles) {
    if (merged.length >= limit) break;
    if (!merged.some((t) => normalize(t) === normalize(tag))) merged.push(tag);
  }

  return merged.slice(0, limit);
}
